import {
  autoUpdate,
  AutoUpdateOptions,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  UseFloatingOptions,
  useTransitionStyles,
} from '@floating-ui/react';

import {useRef} from 'react';

import {useBoolean, useOutsideClick} from 'shared/web';

import {Placement} from '../../../types/Placement';
import {AnimatedPopper, AnimatedPopperProps} from '../AnimatedPopper';

/**
 * Based on https://floating-ui.com/docs/autoupdate#options
 */
const DEFAULT_AUTO_UPDATE_OPTIONS = {
  ancestorResize: true,
  ancestorScroll: true,
  elementResize: true,
  layoutShift: true,
} as const satisfies AutoUpdateOptions;

interface UseAnimatedPopperProps {
  placement?: Placement;
  gutter?: number;
  /**
   * Prevents the popper from closing on outside click
   */
  isOutsideClickIgnored?: boolean;
  strategy?: 'absolute' | 'fixed';
  autoUpdateOptions?: AutoUpdateOptions | 'default';
  /**
   * Use a portal to render the popper outside the DOM hierarchy.
   * Useful when the popper is inside a stacking context (e.g., position: sticky).
   */
  usePortal?: boolean;
  closeOnScroll?: boolean;
}

interface UseAnimatedPopperReturnType {
  isOpen: boolean;
  popperProps: AnimatedPopperProps;
  Popper: typeof AnimatedPopper;
  openPopper: () => void;
  closePopper: () => void;
  togglePopper: () => void;
  referenceRef: <T extends Element>(node: T | null) => void;
}

const DEFAULT_PROPS: UseAnimatedPopperProps = {
  placement: 'top-start',
  gutter: 0,
  strategy: 'absolute',
};

export const useAnimatedPopper = (
  props: UseAnimatedPopperProps = DEFAULT_PROPS
): UseAnimatedPopperReturnType => {
  const clickRef = useRef<HTMLDivElement>(null);
  const [isOpen, onOpen, onClose, onToggle] = useBoolean(false);

  const whileElementsMounted: UseFloatingOptions['whileElementsMounted'] | undefined =
    props.autoUpdateOptions
      ? (referenceEl, floatingEl, update) => {
          const autoUpdateOptions =
            props.autoUpdateOptions === 'default'
              ? DEFAULT_AUTO_UPDATE_OPTIONS
              : props.autoUpdateOptions;

          return autoUpdate(referenceEl, floatingEl, update, autoUpdateOptions);
        }
      : undefined;

  const {refs, floatingStyles, context} = useFloating({
    whileElementsMounted,
    placement: props.placement,
    middleware: [offset(props.gutter), shift(), flip()],
    strategy: props.strategy,
    open: isOpen,
    onOpenChange: (open) => (open ? onOpen() : onClose()),
  });

  const {styles: transitionStyles} = useTransitionStyles(context);

  useOutsideClick({
    ref: clickRef,
    handler: onClose,
    enabled: !props.isOutsideClickIgnored,
  });

  useDismiss(context, {
    ancestorScroll: props.closeOnScroll ?? false,
    // NOTE: Possibly replace useOutsideClick with outsidePress
    // - Add useInteractions hook, update consumers (might conflict with existing handlers
    // - Refactoring is not worth it right now, maybe in the future if we needed more interactions via useInteractions hook
    // outsidePress: false,
  });

  return {
    isOpen,
    openPopper: onOpen,
    closePopper: onClose,
    togglePopper: onToggle,
    referenceRef: refs.setReference,
    Popper: AnimatedPopper,
    popperProps: {
      isOpen,
      floatingRef: refs.setFloating,
      clickRef,
      floatingStyles,
      transitionStyles,
      usePortal: props.usePortal,
    },
  };
};
